import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertTriangle, Save } from 'lucide-react';

// Predefined reasons (single source of truth)
export var RECOVERY_REASONS = [
  'Penalty',
  'Late Mark',
  'Asset Damage',
  'Overpayment',
  'Advance / Loan',
  'Notice Period Short',
  'PF Catch-up',
  'Insurance Premium',
  'Tax Adjustment',
  'Other',
];

export default function RecoveryEditForm(props) {
  var record = props.record;
  var onSave = props.onSave;

  var amountState = useState(String(record.recovery_amount || ''));
  var amount = amountState[0];
  var setAmount = amountState[1];

  var reasonState = useState(record.recovery_reason || '');
  var reason = reasonState[0];
  var setReason = reasonState[1];

  var commentState = useState(record.recovery_comment || '');
  var comment = commentState[0];
  var setComment = commentState[1];

  var netPayable = record.net_payable || record.final_payable_salary || 0;
  var itAmount = record.income_tax_amount || 0;
  var amtNum = parseFloat(amount) || 0;
  var bankPreview = Math.max(0, netPayable - itAmount - amtNum);

  function fmt(n) {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);
  }

  return (
    <div className="space-y-4" data-testid="recovery-edit-form">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-rose-600" />
        <h4 className="font-semibold text-sm">Recovery Deduction</h4>
      </div>

      <div className="space-y-3">
        <div>
          <Label className="text-xs text-muted-foreground">Amount (₹)</Label>
          <Input
            type="number"
            value={amount}
            onChange={function(e) { setAmount(e.target.value); }}
            className="h-8 text-sm mt-1 font-mono"
            placeholder="0"
            data-testid="recovery-amount-input"
          />
        </div>

        <div>
          <Label className="text-xs text-muted-foreground">Reason</Label>
          <Select value={reason} onValueChange={setReason} disabled={amtNum === 0}>
            <SelectTrigger className="h-8 text-sm mt-1" data-testid="recovery-reason-select">
              <SelectValue placeholder="Select reason…" />
            </SelectTrigger>
            <SelectContent>
              {RECOVERY_REASONS.map(function(r) {
                return <SelectItem key={r} value={r}>{r}</SelectItem>;
              })}
            </SelectContent>
          </Select>
        </div>

        {(reason === 'Other' || amtNum > 0) && (
          <div>
            <Label className="text-xs text-muted-foreground">
              Remarks <span className="text-[10px]">(optional)</span>
            </Label>
            <Textarea
              value={comment}
              onChange={function(e) { setComment(e.target.value); }}
              className="text-sm mt-1 min-h-[52px]"
              placeholder="e.g. broken laptop screen — replacement cost split"
              rows={2}
              data-testid="recovery-comment-input"
            />
          </div>
        )}

        {amtNum > 0 && (
          <div className="bg-slate-50 rounded-lg p-3 space-y-1.5 border">
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">Net Payable</span>
              <span className="font-mono">{fmt(netPayable)}</span>
            </div>
            {itAmount > 0 && (
              <div className="flex justify-between text-xs text-indigo-600">
                <span>Income Tax</span>
                <span className="font-mono">-{fmt(itAmount)}</span>
              </div>
            )}
            <div className="flex justify-between text-xs text-rose-600">
              <span>Recovery</span>
              <span className="font-mono">-{fmt(amtNum)}</span>
            </div>
            <div className="border-t pt-1.5 flex justify-between text-sm font-semibold text-emerald-700">
              <span>Bank Transfer</span>
              <span className="font-mono">{fmt(bankPreview)}</span>
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-2">
        {amtNum > 0 && (
          <Button
            size="sm"
            variant="outline"
            className="flex-1"
            onClick={function() { onSave(0, '', ''); }}
            data-testid="recovery-clear-btn"
          >
            Clear
          </Button>
        )}
        <Button
          size="sm"
          className="flex-1 gap-1.5 bg-rose-600 hover:bg-rose-700"
          onClick={function() { onSave(amount, reason, comment); }}
          disabled={amtNum > 0 && !reason}
          data-testid="recovery-save-btn"
        >
          <Save className="h-3.5 w-3.5" /> Save
        </Button>
      </div>
    </div>
  );
}
